import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import InfovaSwal from '../utils/swal';
import MouseLightEffect from './MouseLightEffect';
import { useTrack } from '../analytics/TrackingContext';
import { useFeatureFlags } from '../features/FeatureFlagsContext';

function ComparativaCostes({ gastoMensualCarga, gastoMensualCombustible, nombreVehiculoElectrico, nombreVehiculoCombustion, onClose }) {
    const { trackEvent } = useTrack();
    const { isEnabled } = useFeatureFlags();
    const [mensualidades, setMensualidades] = useState('');
    const [cuotaMensualElectrico, setCuotaMensualElectrico] = useState('');
    const [cuotaMensualCombustion, setCuotaMensualCombustion] = useState('');
    const [costesAdicionalesElectrico, setCostesAdicionalesElectrico] = useState('');
    const [costesAdicionalesCombustion, setCostesAdicionalesCombustion] = useState('');
    const [planMoves, setPlanMoves] = useState('');
    const [resultadoFinalElectrico, setResultadoFinalElectrico] = useState(null);
    const [resultadoFinalCombustion, setResultadoFinalCombustion] = useState(null);
    const [animarResultado, setAnimarResultado] = useState(false); // Controla la animación

    const comparativaRef = useRef(null); // Referencia para la captura de pantalla

    // Estado para habilitar/deshabilitar el componente
    const electricoDisponible = gastoMensualCarga !== null && !isNaN(gastoMensualCarga);
    const combustionDisponible = gastoMensualCombustible !== null && !isNaN(gastoMensualCombustible);
    const activado = electricoDisponible && combustionDisponible;

    // Coste mensual total dinámico
    const costeMensualElectrico =
        parseFloat(cuotaMensualElectrico || 0) +
        parseFloat(gastoMensualCarga || 0) +
        parseFloat(costesAdicionalesElectrico || 0) / 12;
    const costeMensualCombustion =
        parseFloat(cuotaMensualCombustion || 0) +
        parseFloat(gastoMensualCombustible || 0) +
        parseFloat(costesAdicionalesCombustion || 0) / 12;

    // Función para calcular el resultado final con Plan Moves
    const calcularResultados = () => {
        if (!mensualidades || !planMoves) {
            trackEvent('calculation_error', {
                screen: 'calculator', section: 'compara_gastos', tool: 'comparativa_costes',
                properties: { error_code: 'missing_required' },
            });
            InfovaSwal.fire({ icon: 'warning', title: 'Campos requeridos', text: 'Por favor introduce las mensualidades y las ayudas.' });
            return;
        }

        const planMovesReducido = parseFloat(planMoves) / parseFloat(mensualidades);
        setResultadoFinalElectrico(parseFloat(costeMensualElectrico) - parseFloat(planMovesReducido));
        setResultadoFinalCombustion(parseFloat(costeMensualCombustion));
        trackEvent('calculation_success', {
            screen: 'calculator', section: 'compara_gastos', tool: 'comparativa_costes',
        });
    };

    useEffect(() => {
        if (!activado) {
            setResultadoFinalElectrico(null);
            setResultadoFinalCombustion(null);
            setAnimarResultado(true); // Activar la animación
        }
    }, [activado]);

const añadirPaginaLegal = (pdf) => {
    pdf.addPage();
    const margin = 14;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;
    let y = 16;

    const block = (text, fontSize, bold, indent = 0) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        const lines = pdf.splitTextToSize(text, maxWidth - indent);
        lines.forEach(line => {
            if (y > 280) { pdf.addPage(); y = 16; }
            pdf.text(line, margin + indent, y);
            y += fontSize * 0.45;
        });
        y += 1.5;
    };

    block('AVISO LEGAL — CONDICIONES DE USO DEL SIMULADOR', 11, true);
    y += 2;
    block('1. NATURALEZA Y FINALIDAD DE LA SIMULACIÓN', 9, true);
    block('1.1. El presente simulador tiene carácter meramente informativo y orientativo. Su finalidad es ofrecer al usuario (en adelante, el "Usuario") una estimación comparativa del precio y los costes de uso de un vehículo de la Gama 0 Emisiones Kia (vehículo eléctrico –VE– o híbrido enchufable–PHEV–) frente a otro u otros vehículos seleccionados.', 8, false);
    block('1.2. Los resultados obtenidos no constituyen oferta comercial vinculante, presupuesto ni compromiso contractual alguno por parte del Concesionario. Las condiciones definitivas de adquisición se determinarán, en su caso, en el correspondiente contrato de compraventa.', 8, false);
    y += 2;
    block('2. ORIGEN Y TRATAMIENTO DE LOS DATOS', 9, true);
    block('2.1. Los datos utilizados en la simulación proceden de las siguientes fuentes:', 8, false);
    block('a) Datos facilitados por el Usuario al comercial del Concesionario: precio del combustible (diésel o gasolina), kilómetros diarios recorridos de lunes a domingo, posibilidad de realizar cargas completas de batería durante un día laborable y prestaciones o equipamiento deseados.', 8, false, 4);
    block('b) Datos del vehículo: precio (IVA incluido), impuesto de matriculación y demás impuestos, en su caso, a determinar, acabado, motorización y consumo medio homologado conforme al ciclo WLTP, obtenidos de la página web oficial de la marca o de la documentación técnica oficial del vehículo seleccionado por el Usuario.', 8, false, 4);
    block('c) Precio de la energía eléctrica: obtenido de la página web de la empresa distribuidora de electricidad indicada por el Usuario o, en su defecto, del precio medio de la electricidad a nivel nacional publicado por fuentes oficiales.', 8, false, 4);
    block('2.2. El Concesionario ha actuado con la diligencia debida en la obtención e incorporación de los datos al simulador. No obstante, no puede garantizar la exactitud absoluta de los datos proporcionados por el Usuario ni de aquellos extraídos de fuentes de terceros, al depender de información actualizada por dichos terceros en sus respectivas páginas web o documentación.', 8, false);
    block('2.3. El Concesionario no mantiene vinculación alguna con los terceros cuyos datos o páginas web se utilicen como fuente para la simulación.', 8, false);
    y += 2;
    block('3. ESTIMACIÓN DE COSTES DE ENERGÍA', 9, true);
    block('3.1. La estimación de coste anual en electricidad o combustible (expresada en euros) se calcula sobre la base del coste diario de energía, conforme a los siguientes parámetros facilitados por el Usuario:', 8, false);
    block('a) Kilómetros diarios recorridos de lunes a domingo.', 8, false, 4);
    block('b) Precio del combustible (diésel o gasolina) indicado por el Usuario.', 8, false, 4);
    block('c) Posibilidad de realizar cargas completas de batería eléctrica durante un día laborable, entendiéndose por carga completa la necesaria para alcanzar el 100 % de la autonomía eléctrica según la homologación WLTP del vehículo.', 8, false, 4);
    block('3.2. El consumo medio homologado del vehículo comparado corresponde al dato oficial publicado en la página web de la marca o en la documentación técnica oficial del vehículo, conforme al procedimiento de ensayo WLTP. El consumo real puede variar en función de las condiciones de conducción, climatología, estilo de conducción y otros factores.', 8, false);
    y += 2;
    block('4. LIMITACIÓN DE RESPONSABILIDAD', 9, true);
    block('4.1. El Concesionario no será responsable de las inexactitudes que puedan derivarse de:', 8, false);
    block('a) Datos incorrectos, incompletos o desactualizados facilitados por el Usuario.', 8, false, 4);
    block('b) Variaciones en los precios de combustible, electricidad o del vehículo posteriores a la fecha de realización de la simulación.', 8, false, 4);
    block('c) Errores u omisiones contenidos en las páginas web oficiales o documentación de terceros consultadas a la fecha indicada en la simulación.', 8, false, 4);
    block('4.2. La presente limitación de responsabilidad no afectará en ningún caso a los derechos que la legislación vigente en materia de protección de consumidores y usuarios reconoce al Usuario con carácter irrenunciable.', 8, false);
};

const generarPDF = () => {
    const panelActivo = document.querySelector('.tab-panel.active');
    const header = document.querySelector('header');
    const asesorHeaderText = document.querySelector('.asesor-header-text');
    // const logo = document.querySelector('.LogoH');
    const aviso = document.querySelector('.section-container-aviso');

    if (!panelActivo) {
        trackEvent('pdf_generation_error', {
            screen: 'calculator', section: 'compara_gastos', tool: 'comparativa_costes',
            properties: { error_code: 'content_unavailable', format: 'pdf' },
        });
        console.error("Error: No se encontró el panel activo.");
        InfovaSwal.fire({ icon: 'error', title: 'Error', text: 'No se puede generar el PDF en este momento.' });
        return;
    }

    // Crear un contenedor temporal para el PDF
    const tempDiv = document.createElement('div');
    tempDiv.style.background = '#fff';
    tempDiv.style.padding = '24px';
    tempDiv.style.width = panelActivo.offsetWidth + 'px';
    tempDiv.style.boxSizing = 'border-box';
    tempDiv.style.width = '1000px'; // Aumentar el ancho base
    tempDiv.style.fontSize = '16px'; // Aumentar el tamaño base de la fuente

    // Clonar y añadir elementos
    // if (logo) {
    //     const logoClone = logo.cloneNode(true);
    //     logoClone.style.marginBottom = '16px';
    //     tempDiv.appendChild(logoClone);
    // }
    if (header) {
        const headerClone = header.cloneNode(true);
        headerClone.style.marginBottom = '16px';
        tempDiv.appendChild(headerClone);
    }

    if (asesorHeaderText && asesorHeaderText.textContent.trim()) {
        const asesorInfo = document.createElement('div');
        asesorInfo.textContent = `Comercial / Concesionario: ${asesorHeaderText.textContent.replace(/\s+/g, ' ').trim()}`;
        asesorInfo.style.marginBottom = '16px';
        asesorInfo.style.textAlign = 'center';
        asesorInfo.style.fontSize = '16px';
        asesorInfo.style.fontWeight = 'bold';
        asesorInfo.style.color = '#000000';
        tempDiv.appendChild(asesorInfo);
    }

    const panelClone = panelActivo.cloneNode(true);
    tempDiv.appendChild(panelClone);

    if (aviso) {
        const avisoClone = aviso.cloneNode(true);
        avisoClone.style.marginTop = '24px';
        tempDiv.appendChild(avisoClone);
    }

    // Ocultar botones y ajustar estilos
    const botones = tempDiv.querySelectorAll('button');
    botones.forEach(boton => boton.style.display = 'none');

    const elementosTexto = tempDiv.querySelectorAll('*');
    elementosTexto.forEach(elemento => {
        elemento.style.setProperty('color', '#000000', 'important');
        // Forzar color negro específicamente para los resultados
        if (elemento.closest('.res')) {
            elemento.style.setProperty('color', '#000000', 'important');
            elemento.style.setProperty('opacity', '1', 'important');
        }
        if (elemento.tagName.toLowerCase() === 'strong') {
            elemento.style.setProperty('font-weight', 'bold', 'important');
            elemento.style.setProperty('color', '#000000', 'important');
        }

        // Ajustar tamaños según el tipo de elemento
        if (elemento.tagName === 'H3') {
            elemento.style.setProperty('font-size', '24px', 'important');
        }
        if (elemento.closest('.res')) {
            elemento.style.setProperty('color', '#000000', 'important');
            elemento.style.setProperty('opacity', '1', 'important');
            elemento.style.setProperty('font-size', '18px', 'important');
        }
        if (elemento.tagName.toLowerCase() === 'strong') {
            elemento.style.setProperty('font-weight', 'bold', 'important');
            elemento.style.setProperty('color', '#000000', 'important');
            elemento.style.setProperty('font-size', '20px', 'important');
        }
        if (elemento.tagName === 'P') {
            elemento.style.setProperty('font-size', '16px', 'important');
        }
        if (elemento.tagName === 'LABEL') {
            elemento.style.setProperty('font-size', '16px', 'important');
        }
    });

        // Ajustar estilos de los contenedores de input
    const inputContainers = tempDiv.querySelectorAll('.input');
    inputContainers.forEach(container => {
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'space-between';
        container.style.marginBottom = '15px';
        container.style.padding = '8px 0';
    });

    // Ajustar estilos de las etiquetas
    const labels = tempDiv.querySelectorAll('.input label');
    labels.forEach(label => {
        label.style.flex = '0 0 60%';
        label.style.marginRight = '20px';
        label.style.textAlign = 'right';
        label.style.fontSize = '16px';
        label.style.fontWeight = 'normal';
    });

    // Ajustar valores de input (convertidos a span)
    const inputs = tempDiv.querySelectorAll('input');
    inputs.forEach(input => {
        const valor = input.value;
        const span = document.createElement('span');
        span.textContent = valor;
        span.style.flex = '0 0 30%';
        span.style.textAlign = 'left';
        span.style.color = '#000000';
        span.style.fontWeight = 'bold';
        span.style.fontSize = '18px';
        span.style.minWidth = '100px';
        input.parentNode.replaceChild(span, input);
    });

    // Ajustar alineación de las columnas
    const columnas = tempDiv.querySelectorAll('.columna');
    columnas.forEach(columna => {
        columna.style.padding = '20px';
        columna.style.display = 'flex';
        columna.style.flexDirection = 'column';
        columna.style.alignItems = 'stretch';
    });

    // Asegurarse de que los párrafos dentro de .res sean negros
    const resultados = tempDiv.querySelectorAll('.res p');
    resultados.forEach(resultado => {
        resultado.style.setProperty('color', '#000000', 'important');
        resultado.style.setProperty('opacity', '1', 'important');
    });

    const cajitasDiferencia = tempDiv.querySelectorAll('.cajita-diferencia');
    cajitasDiferencia.forEach(cajita => {
        cajita.style.display = 'none';
    });

    tempDiv.style.position = 'fixed';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    // Configuración optimizada de html2canvas
    const canvasOptions = {
        scale: 2.5, // Reducido de 2 a 1.5 para menor tamaño
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        windowWidth: tempDiv.offsetWidth,
        windowHeight: tempDiv.offsetHeight,
        imageTimeout: 0,
        removeContainer: true
    };

    html2canvas(tempDiv, canvasOptions)
    .then((canvas) => {
        // Convertir a JPEG con compresión optimizada
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true // Activar compresión del PDF
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 20; // Margen de 10mm en cada lado
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Si la altura es mayor que la página, ajustamos por altura
        if (imgHeight > pageHeight - 20) {
            const ratio = (pageHeight - 20) / imgHeight;
            pdf.addImage(
                imgData, 
                'JPEG', 
                10, 
                10, 
                imgWidth * ratio, 
                pageHeight - 20,
                null,
                'FAST'
            );
        } else {
            const y = (pageHeight - imgHeight) / 2;
            pdf.addImage(
                imgData, 
                'JPEG', 
                10, 
                y, 
                imgWidth, 
                imgHeight,
                null,
                'FAST'
            );
        }

        // --- Página 2: Nota legal ---
        añadirPaginaLegal(pdf);

        pdf.save('Comparativa_costes.pdf');
        trackEvent('pdf_generated', {
            screen: 'calculator', section: 'compara_gastos', tool: 'comparativa_costes',
            properties: { format: 'pdf' },
        });
        InfovaSwal.fire({ icon: 'success', title: 'PDF generado', text: 'El archivo se ha descargado correctamente.' });
    })
    .catch((error) => {
        trackEvent('pdf_generation_error', {
            screen: 'calculator', section: 'compara_gastos', tool: 'comparativa_costes',
            properties: { error_code: 'render_failed', format: 'pdf' },
        });
        console.error("Error al generar el PDF:", error);
        InfovaSwal.fire({ icon: 'error', title: 'Error', text: 'Hubo un error al generar el PDF.' });
    })
    .finally(() => {
        document.body.removeChild(tempDiv);
    });
};

    return (
        <MouseLightEffect>
        <div ref={comparativaRef} className="comparativa-container">
        <div className={`section-container comparativa-costes section-container-comparativa ${!activado ? 'desactivado' : ''}`}>
            {onClose && (
              <div className="tooltip-wrapper" style={{ position: 'absolute', top: '12px', right: '12px' }}>
                <button
                  onClick={onClose}
                  style={{
                    background: 'transparent',
                    border: '1px solid #ccc',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
                <span className="tooltip-text tooltip-left">Eliminar comparativa</span>
              </div>
            )}
            <h2>Compara El Coste Real Del Vehiculo Eléctrico</h2>
            <div className="comparativa-grid">
                {/* Columna vehículo eléctrico */}
                <div className="columna electrico">
                    <h3>Vehículo Eléctrico</h3>
                    {nombreVehiculoElectrico && <h4 style={{ textAlign: 'center', margin: '-8px 0 12px', fontStyle: 'italic' }}>{nombreVehiculoElectrico}</h4>}
                    <div className="input plan-moves">
                        <label htmlFor="mensualidades_input">Mensualidades:</label>
                        <input
                            id="mensualidades_input"
                            type="number"
                            value={mensualidades}
                            onChange={(e) => setMensualidades(e.target.value)}
                            placeholder="Ej: 60"
                            min="1"
                            disabled={!activado}
                        />
                    </div>
                    <div className="input">
                        <label htmlFor="cuota_input">Cuota Mensual (€):</label>
                        <input
                            id="cuota_input"
                            type="number"
                            value={cuotaMensualElectrico}
                            onChange={(e) => setCuotaMensualElectrico(e.target.value)}
                            placeholder="Ej: 300"
                            min="0"
                            disabled={!activado}
                        />
                    </div>
                    <div className="input">
                        <label htmlFor="costes_ad_input">Costes Adicionales Anuales (€/año):</label>
                        <input
                            id="costes_ad_input"
                            type="number"
                            value={costesAdicionalesElectrico}
                            onChange={(e) => setCostesAdicionalesElectrico(e.target.value)}
                            placeholder="Ej: 600"
                            min="0"
                            disabled={!activado}
                        />
                    </div>
                    {activado && costesAdicionalesElectrico ? (
                        <div className="res" style={{ padding: '4px 0', marginBottom: '8px' }}>
                            <p style={{ margin: 0, fontSize: '0.9em' }}>
                                Costes adicionales mensuales: <strong>{(parseFloat(costesAdicionalesElectrico) / 12).toFixed(2)} €/mes</strong>
                            </p>
                        </div>
                    ) : null}
                    <div className="resultado-coste-mensual">
                        <p>Coste Mensual Total Eléctrico:</p>
                        <p><strong>{activado ? costeMensualElectrico.toFixed(2) : '--'} €</strong></p>
                    </div>
                    <div className="input">
                        <label htmlFor="ayudas_input">Ayudas (€):</label>
                        <input
                            id="ayudas_input"
                            type="number"
                            value={planMoves}
                            onChange={(e) => setPlanMoves(e.target.value)}
                            placeholder="Ej: 3000"
                            min="0"
                            disabled={!activado}
                        />
                    </div>
                </div>

                {/* Columna vehículo de combustión */}
                <div className="columna combustion">
                    <h3>Vehículo de Combustión</h3>
                    {nombreVehiculoCombustion && <h4 style={{ textAlign: 'center', margin: '-8px 0 12px', fontStyle: 'italic' }}>{nombreVehiculoCombustion}</h4>}
                    <div className="input plan-moves">
                        <label htmlFor="mens_gas_input">Mensualidades:</label>
                        <input
                            id="mens_gas_input"
                            type="number"
                            value={mensualidades}
                            onChange={(e) => setMensualidades(e.target.value)}
                            placeholder="Ej: 60"
                            min="1"
                            disabled={!activado}
                        />
                    </div>
                    <div className="input">
                        <label htmlFor="cuota_gas_input">Cuota Mensual (€):</label>
                        <input
                            id="cuota_gas_input"
                            type="number"
                            value={cuotaMensualCombustion}
                            onChange={(e) => setCuotaMensualCombustion(e.target.value)}
                            placeholder="Ej: 250"
                            min="0"
                            disabled={!activado}
                        />
                    </div>
                    <div className="input">
                        <label htmlFor="costes_ad_gas_input">Costes Adicionales Anuales (€/año):</label>
                        <input
                            id="costes_ad_gas_input"
                            type="number"
                            value={costesAdicionalesCombustion}
                            onChange={(e) => setCostesAdicionalesCombustion(e.target.value)}
                            placeholder="Ej: 960"
                            min="0"
                            disabled={!activado}
                        />
                    </div>
                    {activado && costesAdicionalesCombustion ? (
                        <div className="res" style={{ padding: '4px 0', marginBottom: '8px' }}>
                            <p style={{ margin: 0, fontSize: '0.9em' }}>
                                Costes adicionales mensuales: <strong>{(parseFloat(costesAdicionalesCombustion) / 12).toFixed(2)} €/mes</strong>
                            </p>
                        </div>
                    ) : null}
                    <div className="resultado-coste-mensual">
                        <p>Coste Mensual Total Combustión:</p>
                        <p><strong>{activado ? costeMensualCombustion.toFixed(2) : '--'} €</strong></p>
                    </div>
                </div>
            </div>

            {/* Botón para calcular resultado final */}
            <div className="button">
                <button className="calc" onClick={calcularResultados} disabled={!activado}>
                    Calcular Comparativa
                </button>
            </div>

            {/* Resultados finales */}
            {resultadoFinalElectrico !== null && resultadoFinalCombustion !== null && (
                <div className={`res ${animarResultado ? 'resultado-animado' : ''}`}>
                    <p>Resultado Final Vehículo Eléctrico con Ayudas:</p>
                    <p><strong>{resultadoFinalElectrico.toFixed(2)} €</strong></p>
                    <p>Resultado Final Vehículo de Combustión:</p>
                    <p><strong>{resultadoFinalCombustion.toFixed(2)} €</strong></p>
                    {isEnabled('pdf_comparativa') && <button className="calc" onClick={generarPDF}>Descargar Comparativa</button>}
                </div>
            )}
        </div>
        </div>
        </MouseLightEffect>
    );
}

export default ComparativaCostes;
